/**
 * Tracking Service - Background Geolocation "Strava-class"
 * Handles GPS tracking with high accuracy, background mode, and motion detection
 */

import BackgroundGeolocation, { Location } from '@transistorsoft/capacitor-background-geolocation';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { KalmanFilter } from './kalman-filter-service';
import { snapToRoad } from './snap-to-road-service';
import { supabase } from '@/integrations/supabase/client';
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import * as Sentry from '@sentry/react';
import { logger } from '@/lib/logger';
import { persistentLogger } from '@/lib/persistent-logger';
import { StorageAdapter, IndexedDBStorageAdapter, InMemoryStorageAdapter } from './storage-adapter';

interface TrackingPoint {
  lat: number;
  lng: number;
  timestamp: number;
  speed: number;
  accuracy: number;
  altitude?: number;
}

interface MockGPSPoint {
  lat: number;
  lng: number;
  timestamp?: number;
  speed?: number;
  accuracy?: number;
  altitude?: number;
}

interface TrackingMetrics {
  p95Error: number;
  gapPercentage: number;
  batteryDrain: number;
  reconnectionTime: number;
}

interface TrackingDB extends DBSchema {
  points: {
    key: number;
    value: TrackingPoint & { sessionId: string };
    indexes: { 'by-session': string };
  };
}

class TrackingService {
  private kalmanFilter: KalmanFilter;
  private isConfigured = false;
  private sessionId: string | null = null;
  private points: TrackingPoint[] = [];
  private pointsBuffer: TrackingPoint[] = []; // Buffer para batch sync
  private batchSyncTimer: number | null = null;
  private db: IDBPDatabase<TrackingDB> | null = null;
  private storage: StorageAdapter;
  private watchId: string | null = null;
  private isNative: boolean = false;
  private bgWatcherId: string | null = null;
  private accuracyHistory: number[] = [];
  private rejectedPointsLog = {
    accuracy: 0,
    speed: 0,
    distance: 0
  };
  private metrics: TrackingMetrics = {
    p95Error: 0,
    gapPercentage: 0,
    batteryDrain: 0,
    reconnectionTime: 0
  };
  
  // Location listeners for instant UI updates (Strava-class performance)
  private locationListeners: Array<(point: TrackingPoint) => void> = [];
  
  // ⏱️ FORCED 1Hz COLLECTION: Timer for time-based GPS polling (independent of movement)
  private forcedCollectionTimer: ReturnType<typeof setInterval> | null = null;
  private forcedCollectionEnabled: boolean = false;

  constructor(storage?: StorageAdapter) {
    this.kalmanFilter = new KalmanFilter();
    this.isNative = Capacitor.isNativePlatform();
    
    if (storage) {
      // Test: use injected storage
      this.storage = storage;
    } else {
      // Production: create placeholder until initDB completes
      this.storage = {
        add: async () => {},
        getAll: async () => [],
        clear: async () => {},
        count: async () => 0
      } as StorageAdapter;
    }
    
    this.initDB(storage);
    
    logger.info('TrackingService initialized', {
      platform: Capacitor.getPlatform(),
      isNative: this.isNative,
      isEmulator: this.isRunningOnEmulator()
    });
  }

  /**
   * Check if tracking is currently active
   * @returns true if watchId or bgWatcherId is set and sessionId exists
   */
  public isActive(): boolean {
    return (this.watchId !== null || this.bgWatcherId !== null) && this.sessionId !== null;
  }

  /**
   * Get current session ID
   * @returns current session ID or null
   */
  public getSessionId(): string | null {
    return this.sessionId;
  }

  // Permission status cache for getDiagnostics
  private lastPermissionStatus: 'unknown' | 'granted' | 'denied' | 'reduced_accuracy' = 'unknown';
  private lastAccuracy: number | null = null;
  private lastHeading: number | null = null; // ✅ Store last heading for UI
  private lastSpeed: number | null = null;
  private lastBearing: number | null = null;
  private isEmergencyModeActive: boolean = false;
  private capacitorFallbackWatchId: string | null = null; // ✅ Fallback nativo via Capacitor
  private noPointsWatchdogTimer: ReturnType<typeof setTimeout> | null = null; // ✅ Watchdog para fallback

  /**
   * Get diagnostic information for UI feedback
   * Provides real-time GPS status including points, accuracy, heading, and permission state
   */
  public getDiagnostics(): {
    pointsCollected: number;
    rejectedPoints: { accuracy: number; speed: number; distance: number; total: number };
    lastAccuracy: number | null;
    lastHeading: number | null; // GPS course/heading (quando disponível)
    lastSpeed: number | null;
    lastBearing: number | null; // bearing calculado entre os 2 últimos pontos válidos
    permissionStatus: 'unknown' | 'granted' | 'denied' | 'reduced_accuracy';
    isEmergencyMode: boolean;
    sessionStartTime: number;
  } {
    // Check if emergency mode should be active (no points after 5s)
    const timeSinceStart = this.sessionStartTime > 0 
      ? (Date.now() - this.sessionStartTime) / 1000 
      : 0;
    this.isEmergencyModeActive = this.points.length === 0 && timeSinceStart > 5;

    return {
      pointsCollected: this.points.length,
      rejectedPoints: {
        ...this.rejectedPointsLog,
        total: this.rejectedPointsLog.accuracy + this.rejectedPointsLog.speed + this.rejectedPointsLog.distance
      },
      lastAccuracy: this.lastAccuracy,
      lastHeading: this.lastHeading,
      lastSpeed: this.lastSpeed,
      lastBearing: this.lastBearing,
      permissionStatus: this.lastPermissionStatus,
      isEmergencyMode: this.isEmergencyModeActive,
      sessionStartTime: this.sessionStartTime,
    };
  }

  private calculateBearingDeg(fromLat: number, fromLng: number, toLat: number, toLng: number): number {
    // Bearing inicial (0..360)
    const φ1 = (fromLat * Math.PI) / 180;
    const φ2 = (toLat * Math.PI) / 180;
    const Δλ = ((toLng - fromLng) * Math.PI) / 180;

    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    const θ = Math.atan2(y, x);
    const deg = (θ * 180) / Math.PI;
    return ((deg % 360) + 360) % 360;
  }

  /**
   * Update permission status (called after permission checks)
   */
  public setPermissionStatus(status: 'unknown' | 'granted' | 'denied' | 'reduced_accuracy') {
    this.lastPermissionStatus = status;
  }

  /**
   * Detecta se está rodando em emulador Android
   */
  private isRunningOnEmulator(): boolean {
    if (!this.isNative) return false;
    
    try {
      const userAgent = navigator.userAgent.toLowerCase();
      const isEmulator = userAgent.includes('emulator') || 
                        userAgent.includes('sdk_gphone') ||
                        userAgent.includes('vbox') ||
                        userAgent.includes('generic') ||
                        userAgent.includes('android sdk built for');
      
      return isEmulator;
    } catch {
      return false;
    }
  }

  private async initDB(providedStorage?: StorageAdapter) {
    // If storage was provided (e.g., for tests), use it
    if (providedStorage) {
      this.storage = providedStorage;
      return;
    }

    // Otherwise, create IndexedDB and wrap it
    try {
      this.db = await openDB<TrackingDB>('ybr-tracking', 1, {
        upgrade(db) {
          const store = db.createObjectStore('points', { keyPath: 'timestamp' });
          store.createIndex('by-session', 'sessionId');
        },
      });
      
      // Pass the initialized DB to the adapter to avoid re-initialization
      this.storage = new IndexedDBStorageAdapter(this.db as unknown as IDBDatabase);
    } catch (error) {
      logger.error('Failed to initialize IndexedDB', error, {
        component: 'TrackingService',
        action: 'initDB'
      });
      if (Sentry) {
        Sentry.captureException(error);
      }
      // Fallback to in-memory storage
      this.storage = new InMemoryStorageAdapter();
    }
  }

  async configure() {
    if (this.isConfigured) return;
    try {
      // Detect platform and mark as configured
      // Capacitor v7 exposes isNativePlatform(); keep fallback for compatibility
      this.isNative = typeof (Capacitor as any).isNativePlatform === 'function'
        ? (Capacitor as any).isNativePlatform()
        : (Capacitor.getPlatform && Capacitor.getPlatform() !== 'web');

      this.isConfigured = true;
      logger.info('TrackingService configured', { isNative: this.isNative });
    } catch (error) {
      logger.error('TrackingService: Configuration failed', error, {
        component: 'TrackingService',
        action: 'configure',
      });
      if (Sentry) {
        try { Sentry.captureException(error as any); } catch {}
      }
      try { this.trackEvent('tracking_config_error', { error: String(error) }); } catch {}
      throw error;
    }
  }

  private sessionStartTime: number = 0;
  
  private async handleLocation(position: any) {
    try {
      const accuracy = position.coords.accuracy;
      const speed = position.coords.speed || 0;
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      const timestamp = position.timestamp || Date.now();
      
      // ⏱️ DEDUPLICATION: Prevent duplicate points from forced polling + watcher
      const lastPoint = this.points.length > 0 ? this.points[this.points.length - 1] : null;
      if (lastPoint) {
        const timeDiff = timestamp - lastPoint.timestamp;
        const distance = this.calculateDistance(lastPoint.lat, lastPoint.lng, lat, lng);
        
        // Skip if < 500ms and < 0.5m apart (duplicate from multiple sources)
        if (timeDiff < 500 && distance < 0.5) {
          logger.debug('⏭️ Skipping duplicate point', { timeDiff, distance: distance.toFixed(2) });
          return;
        }
      }
      
      // ✅ ULTRA-RELAXED accuracy filter for IMMEDIATE tracking
      const isFirstPoint = this.points.length === 0;
      const isVeryEarly = this.points.length < 3; // First 3 points
      const isWarmingUp = this.points.length < 20;
      
      // Calculate time since session start
      const timeSinceStart = this.sessionStartTime > 0 
        ? (Date.now() - this.sessionStartTime) / 1000 
        : 0;
      
      // 🆘 EMERGENCY MODE: After 5 seconds (reduced from 15s) with no points, accept anything up to 200m
      const isEmergencyMode = this.points.length === 0 && timeSinceStart > 5;
      
      // ✅ MUCH MORE RELAXED thresholds to eliminate 41s delay:
      // - First point: Accept up to 200m (we MUST show something immediately)
      // - First 3 points: Accept up to 150m (still warming up GPS)
      // - Emergency mode (5s+ no points): Accept up to 200m
      // - Warmup (points 3-20): 100m native / 80m web
      // - Normal: 50m native / 40m web
      let maxAccuracy: number;
      if (isFirstPoint || isEmergencyMode) {
        maxAccuracy = 200; // Accept almost anything for first point or emergency
      } else if (isVeryEarly) {
        maxAccuracy = 200; // Relaxed from 150 for better point collection
      } else if (isWarmingUp) {
        maxAccuracy = this.isNative ? 150 : 100; // Relaxed for short loops
      } else {
        maxAccuracy = this.isNative ? 80 : 60; // Relaxed for better GPS tolerance
      }
      
      // ✅ FILTER 1: Reject only extremely poor accuracy points
      if (accuracy > maxAccuracy) {
        this.rejectedPointsLog.accuracy++;
        
        // Log persistente para debug
        persistentLogger.addLog('GPS', 'warn', 'Point rejected - low accuracy', {
          accuracy,
          maxAllowed: maxAccuracy,
          isWarmingUp,
          isEmergencyMode,
          timeSinceStart: Math.round(timeSinceStart),
          totalRejected: this.rejectedPointsLog.accuracy,
          totalCollected: this.points.length
        });
        
        logger.warn('⚠️ Point rejected: low accuracy', { 
          accuracy,
          maxAccuracy,
          pointsCollected: this.points.length,
          isWarmingUp,
          isEmergencyMode,
          timeSinceStart: Math.round(timeSinceStart),
          totalRejectedByAccuracy: this.rejectedPointsLog.accuracy
        });
        this.metrics.gapPercentage += 1;
        return;
      }
      
      // Log if emergency mode activated
      if (isEmergencyMode && accuracy <= 200) {
        logger.warn('🆘 Emergency mode: accepting point after 5s delay', {
          accuracy,
          timeSinceStart: Math.round(timeSinceStart),
          reason: 'No points collected after 5 seconds'
        });
        persistentLogger.addLog('GPS', 'warn', 'Emergency mode activated - accepting point', {
          accuracy,
          timeSinceStart: Math.round(timeSinceStart)
        });
      }
      
      // Log primeiro ponto aceito
      if (isFirstPoint) {
        logger.info('✅ First GPS point accepted', { accuracy, timeSinceStart: Math.round(timeSinceStart) });
        persistentLogger.addLog('GPS', 'info', 'First GPS point accepted', { accuracy });
      }
      
      // ✅ FILTRO 2: Rejeitar velocidades impossíveis
      // Limite científico: 12.4 m/s = velocidade máxima humana (Usain Bolt)
      // Com margem de erro GPS: 15 m/s
      const MAX_HUMAN_SPEED_WITH_MARGIN = 15; // m/s
      
      if (speed > MAX_HUMAN_SPEED_WITH_MARGIN) {
        this.rejectedPointsLog.speed++;
        logger.warn('⚠️ Point rejected: speed exceeds human capability', { 
          speed,
          maxAllowed: MAX_HUMAN_SPEED_WITH_MARGIN,
          boltRecord: 12.4,
          totalRejectedBySpeed: this.rejectedPointsLog.speed
        });
        return;
      }
      
      // ✅ FILTRO 3: Checar se é outlier geográfico + filtro de aceleração
      if (this.points.length > 0) {
        const lastPoint = this.points[this.points.length - 1];
        const timeDiff = (position.timestamp - lastPoint.timestamp) / 1000; // segundos
        
        if (timeDiff > 0) {
          const distance = this.calculateDistance(
            lastPoint.lat, lastPoint.lng,
            position.coords.latitude, position.coords.longitude
          );
          const calculatedSpeed = distance / timeDiff;
          
          // Rejeitar se velocidade instantânea > limite humano
          if (calculatedSpeed > MAX_HUMAN_SPEED_WITH_MARGIN) {
            this.rejectedPointsLog.distance++;
            logger.warn('⚠️ Point rejected: calculated speed exceeds human capability', { 
              calculatedSpeed,
              distance,
              timeDiff,
              maxAllowed: MAX_HUMAN_SPEED_WITH_MARGIN,
              totalRejectedByDistance: this.rejectedPointsLog.distance
            });
            return;
          }
          
          // ✅ FILTRO 3.5: Rejeitar aceleração impossível
          // Aceleração máxima humana: ~10 m/s² (sprint start)
          // Com margem GPS: 15 m/s²
          const MAX_ACCELERATION = 15; // m/s²
          const lastSpeed = lastPoint.speed || 0;
          const speedChange = Math.abs(calculatedSpeed - lastSpeed);
          
          if (timeDiff > 0.5) { // Só validar se tiver tempo suficiente
            const acceleration = speedChange / timeDiff;
            
            if (acceleration > MAX_ACCELERATION) {
              this.rejectedPointsLog.distance++;
              logger.warn('⚠️ Point rejected: impossible acceleration', { 
                acceleration,
                maxAllowed: MAX_ACCELERATION,
                speedChange,
                timeDiff,
                totalRejectedByDistance: this.rejectedPointsLog.distance
              });
              return;
            }
          }
        }
      }
      
      // ✅ FILTRO 4: Passar pelo Kalman
      const filtered = this.kalmanFilter.filter({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: accuracy
      });

      // ✅ Usar diretamente o ponto filtrado (sem snap-to-road em tempo real)
      // ✅ CRITICAL: Ensure timestamp is always a number (epoch ms), not ISO string
      const normalizedTimestamp = typeof position.timestamp === 'string' 
        ? new Date(position.timestamp).getTime() 
        : position.timestamp;
      
      const point: TrackingPoint = {
        lat: filtered.lat,
        lng: filtered.lng,
        timestamp: normalizedTimestamp,
        speed: speed,
        accuracy: accuracy,
        altitude: position.coords.altitude || undefined
      };

      // Diagnostics: bearing entre o último ponto aceito e o novo ponto
      const prev = this.points.length > 0 ? this.points[this.points.length - 1] : null;
      if (prev) {
        this.lastBearing = this.calculateBearingDeg(prev.lat, prev.lng, point.lat, point.lng);
      }

      this.points.push(point);
      this.pointsBuffer.push(point);
      
      // Update last accuracy and heading for diagnostics
      this.lastAccuracy = accuracy;
      this.lastSpeed = speed;
      // ✅ Capture heading from position if available
      const heading = position.coords.heading;
      if (heading !== null && heading !== undefined && !isNaN(heading)) {
        this.lastHeading = heading;
      }
      
      try {
        localStorage.setItem('lastKnownGPSPosition', JSON.stringify({
          lat: point.lat,
          lng: point.lng,
          timestamp: Date.now(),
          accuracy: point.accuracy,
          heading: this.lastHeading
        }));
      } catch {}
      
      // ✅ Notify location listeners for instant UI updates (Strava-class performance)
      this.notifyLocationListeners(point);
      
      // Log estruturado de ponto coletado
      logger.debug('GPS point collected', {
        pointIndex: this.points.length,
        accuracy,
        speed,
        filtered: { lat: filtered.lat.toFixed(6), lng: filtered.lng.toFixed(6) }
      });
      
      // ✅ BACKUP AGRESSIVO: Primeiros 10 pontos = backup a cada ponto, depois a cada 5
      const backupInterval = this.points.length <= 10 ? 1 : 5;
      if (this.points.length % backupInterval === 0 || this.points.length === 1) {
        try {
          sessionStorage.setItem('gps_points_backup', JSON.stringify(this.points));
          logger.debug('GPS points backed up to sessionStorage', {
            backupPointsCount: this.points.length,
            interval: backupInterval
          });
        } catch (storageError) {
          logger.warn('Failed to backup points to sessionStorage', storageError);
        }
      }
      
      // Persist to IndexedDB
      await this.persistPoint(point);

      // Batch sync: acumular e enviar a cada 15 pontos ou 30 segundos
      if (this.pointsBuffer.length >= 15) {
        await this.batchSyncPoints();
      }

      // Update metrics
      this.updateMetrics(position);

      // ⏱️ REMOVED: No longer adjust distanceFilter after warmup
      // We keep distanceFilter: 0 ALWAYS for time-based 1Hz collection
      // This ensures GPS updates come by TIME, not by movement
      if (this.points.length === 10) {
        logger.info('⚙️ Warmup phase complete (10 points), time-based 1Hz collection active');
      }

      // Analytics
      this.trackEvent('gps_point_recorded', {
        accuracy: accuracy,
        speed: speed
      });
    } catch (error) {
      logger.error('Location handling failed', error, {
        component: 'TrackingService',
        action: 'handleLocation'
      });
      this.trackEvent('gps_point_error', { error: String(error) });
      
      // Reportar erro ao Sentry
      if (typeof window !== 'undefined' && (window as any).Sentry) {
        (window as any).Sentry.captureException(error, {
          tags: { service: 'tracking', action: 'location_handling' }
        });
      }
    }
  }

  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371e3; // Earth radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;
    
    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const distance = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)) * R;
    return distance;
  }

  async startTracking(sessionId: string) {
    const span = logger.startSpan('startTracking', { sessionId });
    
    // ✅ STEP 1: Request permission explicitly (iOS two-step flow)
    if (this.isNative) {
      try {
        logger.info('🔐 Requesting location permission...');
        const state = await BackgroundGeolocation.requestPermission();
        logger.info('📍 Permission state:', state);
        
        // Check if permission was denied
        if (state === BackgroundGeolocation.AUTHORIZATION_STATUS_DENIED || 
            state === BackgroundGeolocation.AUTHORIZATION_STATUS_RESTRICTED) {
          logger.error('❌ GPS permission denied', { state });
          span.end('error', { reason: 'permission_denied', state });
          throw new Error('GPS_PERMISSION_DENIED');
        }
        
        // If we got "When In Use", schedule a second request for "Always" after 15s
        if (state === BackgroundGeolocation.AUTHORIZATION_STATUS_WHEN_IN_USE) {
          logger.info('⏰ Got "When In Use" permission, will request "Always" in 15s');
          setTimeout(async () => {
            try {
              logger.info('🔄 Requesting "Always" permission (two-step flow)...');
              const alwaysState = await BackgroundGeolocation.requestPermission();
              logger.info('📍 "Always" permission result:', alwaysState);
            } catch (err) {
              logger.warn('Failed to request "Always" permission', err);
            }
          }, 15000);
        }
        
        logger.info('✅ GPS permission granted', { state });
      } catch (permError) {
        logger.error('❌ Permission request failed', permError);
        span.end('error', { reason: 'permission_request_failed' });
        throw permError;
      }
    } else {
      // Web: check permission status
      try {
        const permissionStatus = await this.checkLocationPermission();
        if (!permissionStatus.granted) {
          logger.error('❌ GPS permission denied (web)', { status: permissionStatus });
          span.end('error', { reason: 'permission_denied' });
          throw new Error('GPS permission required to start tracking');
        }
        logger.info('✅ GPS permission granted (web)', { status: permissionStatus });
      } catch (permError) {
        logger.error('❌ Permission check failed', permError);
        span.end('error', { reason: 'permission_check_failed' });
        throw permError;
      }
    }
    
    await this.configure();
    
    this.sessionId = sessionId;
    this.sessionStartTime = Date.now(); // ✅ Marcar início para modo emergência
    this.points = [];
    this.pointsBuffer = [];
    
    // Reset rejected points log
    this.rejectedPointsLog = {
      accuracy: 0,
      speed: 0,
      distance: 0
    };
    
    logger.info('🚀 Starting GPS tracking', { 
      sessionId, 
      isNative: this.isNative,
      platform: Capacitor.getPlatform(),
      isEmulator: this.isRunningOnEmulator(),
      filters: {
        maxAccuracyWarmup: this.isNative ? 80 : 60,
        maxAccuracyNormal: this.isNative ? 50 : 40,
        maxSpeed: 20,
        warmupPoints: 20
      }
    });
    
    persistentLogger.addLog('GPS', 'info', 'GPS tracking started', {
      sessionId,
      platform: Capacitor.getPlatform(),
      isNative: this.isNative
    });

    // Fallback timeout reduced to 10s for faster response
    let bgInitialized = false;
    
    const fallbackTimeout = setTimeout(() => {
      if (!bgInitialized && this.points.length === 0) {
        logger.warn('⏰ Background geolocation timeout (10s), forcing standard GPS fallback');
        // Only start if no watcher exists
        if (!this.watchId && !this.bgWatcherId) {
          this.startStandardGeolocation();
        } else {
          logger.info('Watcher already exists, skipping fallback', { 
            watchId: this.watchId,
            bgWatcherId: this.bgWatcherId
          });
        }
      }
    }, 10000);

    if (this.isNative) {
      // Use TransistorSoft background geolocation for native platforms
      const isEmulator = this.isRunningOnEmulator();
      
      logger.info('Initializing TransistorSoft Background Geolocation', {
        isEmulator,
        deviceInfo: navigator.userAgent
      });
      
      try {
        // ✅ Configuração adaptativa baseada em emulador vs físico
        const config = isEmulator ? {
          // Config SUAVE para emulador (evita overload)
          desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_HIGH,
          distanceFilter: 0, // ⚡ WARMUP: Start with 0 to get first point immediately
          stationaryRadius: 25,
          stopTimeout: 5,
          locationAuthorizationRequest: 'Always' as any,
          activityType: BackgroundGeolocation.ACTIVITY_TYPE_FITNESS,
          stopDetectionDelay: 1,
          disableMotionActivityUpdates: false,
          locationUpdateInterval: 3000, // 3s
          fastestLocationUpdateInterval: 2000, // 2s
          deferTime: 0,
          heartbeatInterval: 120,
          stopOnTerminate: false,
          startOnBoot: false,
          autoSync: false,
          debug: true,
          logLevel: process.env.NODE_ENV === 'production' 
            ? BackgroundGeolocation.LOG_LEVEL_ERROR 
            : BackgroundGeolocation.LOG_LEVEL_VERBOSE,
          notification: {
            title: "YBR - Modo Emulador",
            text: "GPS básico ativado (teste em dispositivo físico para máxima precisão)",
            channelName: "Rastreamento de Corrida",
            priority: BackgroundGeolocation.NOTIFICATION_PRIORITY_LOW
          }
        } : {
          // Config AGRESSIVA para dispositivo físico (máxima precisão)
          // ⏱️ TIME-BASED COLLECTION: GPS must update by TIME, not by MOVEMENT
          desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_NAVIGATION,
          distanceFilter: 0, // ⚡ ALWAYS 0 - collect by time, not distance
          stationaryRadius: 5, // Reduced for better sensitivity
          stopTimeout: 0, // ⚡ NEVER stop - disable stop detection entirely
          disableStopDetection: true, // ⚡ CRITICAL - disable movement-based stop
          locationAuthorizationRequest: 'Always' as any,
          activityType: BackgroundGeolocation.ACTIVITY_TYPE_FITNESS,
          stopDetectionDelay: 0,
          disableElasticity: true,
          disableMotionActivityUpdates: true, // ⚡ Don't depend on motion activity
          locationUpdateInterval: 1000, // 1 second
          fastestLocationUpdateInterval: 500,
          deferTime: 0,
          preventSuspend: true,
          heartbeatInterval: 60,
          stopOnTerminate: false,
          startOnBoot: false,
          autoSync: false,
          maxRecordsToPersist: 1000,
          debug: process.env.NODE_ENV !== 'production',
          logLevel: process.env.NODE_ENV === 'production' 
            ? BackgroundGeolocation.LOG_LEVEL_ERROR 
            : BackgroundGeolocation.LOG_LEVEL_VERBOSE,
          notification: {
            title: "YBR está rastreando sua corrida",
            text: "GPS de alta precisão ativado",
            channelName: "Rastreamento de Corrida",
            priority: BackgroundGeolocation.NOTIFICATION_PRIORITY_LOW
          }
        };

        logger.info('Using TransistorSoft config', { 
          mode: isEmulator ? 'emulator' : 'physical',
          distanceFilter: config.distanceFilter,
          updateInterval: config.locationUpdateInterval
        });

        // ✅ Timeout de segurança + try-catch robusto
        const readyPromise = BackgroundGeolocation.ready(config);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('BackgroundGeolocation.ready() timeout after 10s')), 10000)
        );

        await Promise.race([readyPromise, timeoutPromise]);

        // Location listener
        BackgroundGeolocation.onLocation((location: Location) => {
          logger.debug('Background location received', {
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
            accuracy: location.coords.accuracy,
            activity: location.activity.type
          });

          // Convert to standard format
          const position = {
            coords: {
              latitude: location.coords.latitude,
              longitude: location.coords.longitude,
              accuracy: location.coords.accuracy,
              altitude: location.coords.altitude || null,
              altitudeAccuracy: location.coords.altitude_accuracy || null,
              heading: location.coords.heading || null,
              speed: location.coords.speed || null
            },
            timestamp: location.timestamp
          };

          this.handleLocation(position);
        });

        // Provider change listener
        BackgroundGeolocation.onProviderChange((provider) => {
          logger.warn('Location provider changed', {
            enabled: provider.enabled,
            gps: provider.gps,
            network: provider.network,
            status: provider.status
          });
          
          // Alert user if GPS disabled
          if (!provider.gps && typeof window !== 'undefined' && (window as any).toast) {
            (window as any).toast.error(
              'GPS desativado. Por favor, ative o GPS para continuar rastreando.'
            );
          }
          
          this.trackEvent('provider_change', {
            enabled: provider.enabled,
            gps: provider.gps,
            network: provider.network
          });
        });

        // Activity change listener
        BackgroundGeolocation.onActivityChange((event) => {
          logger.info('Activity detected', {
            activity: event.activity,
            confidence: event.confidence
          });
          
          this.trackEvent('activity_change', {
            activity: event.activity,
            confidence: event.confidence
          });
        });

        // Motion change listener
        BackgroundGeolocation.onMotionChange((event) => {
          logger.info('Motion state changed', {
            isMoving: event.isMoving,
            location: event.location ? {
              lat: event.location.coords.latitude,
              lng: event.location.coords.longitude
            } : null
          });
          
          this.trackEvent('motion_change', {
            isMoving: event.isMoving
          });
        });

        // Power save mode listener
        BackgroundGeolocation.onPowerSaveChange((isPowerSaveMode) => {
          logger.warn('Power save mode changed', { isPowerSaveMode });
          
          if (isPowerSaveMode && typeof window !== 'undefined' && (window as any).toast) {
            (window as any).toast.warning(
              'Modo economia de bateria ativado. Precisão do GPS pode ser reduzida.'
            );
          }
          
          this.trackEvent('power_save_change', { isPowerSaveMode });
        });

        // Start tracking
        await BackgroundGeolocation.start();
        this.bgWatcherId = 'transistorsoft-active';
        bgInitialized = true;
        clearTimeout(fallbackTimeout);
        
        // ✅ Set permission status to granted since we got this far
        this.setPermissionStatus('granted');
        
        logger.info('✅ TransistorSoft background geolocation started successfully', {
          mode: isEmulator ? 'emulator' : 'physical_device'
        });
        
        // ✅ FORCE MOVEMENT STATE (ensure GPS updates even if stationary)
        try {
          await BackgroundGeolocation.changePace(true);
          logger.info('✅ Forced movement state (changePace: true) for continuous updates');
        } catch (paceError) {
          logger.warn('Failed to set changePace', paceError);
        }
        
        // ⏱️ START FORCED 1Hz COLLECTION (time-based polling independent of movement)
        this.startForcedCollection(1000);
        
        // ✅ FORCE FIRST GPS POINT with progressive retry strategy
        logger.info('🎯 Forcing first GPS point via progressive retry strategy...');
        
        // Progressive retry attempts - each more generous than the last
        const firstFixAttempts = [
          { timeout: 3000, accuracy: 100, maximumAge: 30000, label: 'fast' },    // Attempt 1: Quick (cached OK)
          { timeout: 8000, accuracy: 150, maximumAge: 60000, label: 'medium' },  // Attempt 2: More time
          { timeout: 15000, accuracy: 200, maximumAge: 120000, label: 'slow' }   // Attempt 3: Last resort
        ];
        
        let firstLocation = null;
        for (const attempt of firstFixAttempts) {
          try {
            logger.info(`📍 GPS attempt: ${attempt.label} (timeout: ${attempt.timeout}ms)`);
            firstLocation = await BackgroundGeolocation.getCurrentPosition({
              samples: 1,
              timeout: attempt.timeout,
              persist: false,
              maximumAge: attempt.maximumAge,
              desiredAccuracy: attempt.accuracy
            });
            
            if (firstLocation) {
              logger.info(`✅ First GPS point acquired on ${attempt.label} attempt`, {
                coords: { lat: firstLocation.coords.latitude, lng: firstLocation.coords.longitude },
                accuracy: firstLocation.coords.accuracy,
                attemptTimeout: attempt.timeout
              });
              
              // Convert and handle the first point
              this.handleLocation({
                coords: {
                  latitude: firstLocation.coords.latitude,
                  longitude: firstLocation.coords.longitude,
                  accuracy: firstLocation.coords.accuracy,
                  altitude: firstLocation.coords.altitude,
                  altitudeAccuracy: firstLocation.coords.altitude_accuracy,
                  heading: firstLocation.coords.heading,
                  speed: firstLocation.coords.speed
                },
                timestamp: firstLocation.timestamp
              });
              
              break; // Success - exit retry loop
            }
          } catch (err) {
            logger.warn(`⚠️ GPS ${attempt.label} attempt failed`, err);
            persistentLogger.addLog('GPS', 'warn', `First fix ${attempt.label} attempt failed`, {
              timeout: attempt.timeout,
              error: err instanceof Error ? err.message : String(err)
            });
          }
        }
        
        if (!firstLocation) {
          logger.warn('⚠️ All GPS first fix attempts failed (will rely on watchdog fallback)');
          persistentLogger.addLog('GPS', 'warn', 'All first fix attempts exhausted', {});
        }
        
        // ⏱️ REMOVED: No longer switch to distanceFilter after warmup
        // We keep distanceFilter: 0 ALWAYS for time-based collection
        
        // ✅ WATCHDOG: If no points after 8 seconds, activate Capacitor fallback
        this.startNoPointsWatchdog();
        
        // ✅ CHECK PROVIDER STATE (GPS enabled, location services active)
        try {
          const providerState = await BackgroundGeolocation.getProviderState();
          logger.info('📡 Provider state:', {
            enabled: providerState.enabled,
            gps: providerState.gps,
            network: providerState.network,
            status: providerState.status,
            accuracyAuthorization: providerState.accuracyAuthorization
          });
          
          if (!providerState.enabled || !providerState.gps) {
            logger.warn('⚠️ GPS or location services disabled', providerState);
            // Don't throw - let the user know via UI
          }
          
          // ✅ iOS 14+: Check for "Precise Location" toggle (reduced accuracy)
          // ACCURACY_AUTHORIZATION_REDUCED = 1, ACCURACY_AUTHORIZATION_FULL = 0
          if (providerState.accuracyAuthorization === 1) {
            logger.warn('⚠️ Precise Location is OFF - requesting full accuracy for run tracking');
            this.setPermissionStatus('reduced_accuracy');
            
            persistentLogger.addLog('GPS', 'warn', 'Precise Location disabled - requesting upgrade', {});
            
            // Request temporary full accuracy (iOS will show a prompt)
            try {
              await BackgroundGeolocation.requestTemporaryFullAccuracy('FullAccuracyReason');
              logger.info('✅ Requested temporary full accuracy');
              
              // Re-check after request
              const newState = await BackgroundGeolocation.getProviderState();
              if (newState.accuracyAuthorization === 0) {
                logger.info('✅ User granted full accuracy');
                this.setPermissionStatus('granted');
              }
            } catch (accuracyError) {
              logger.warn('Failed to request full accuracy', accuracyError);
            }
          }
        } catch (providerError) {
          logger.warn('Failed to check provider state', providerError);
        }

        if (isEmulator && typeof window !== 'undefined' && (window as any).toast) {
          (window as any).toast.info(
            'Modo emulador detectado. GPS básico ativado. Para máxima precisão, teste em dispositivo físico.'
          );
        }

      } catch (error) {
        clearTimeout(fallbackTimeout);
        // ✅ FALLBACK ROBUSTO: Não crashear, usar GPS padrão
        logger.error('❌ Failed to initialize TransistorSoft', error, {
          component: 'TrackingService',
          action: 'start_background',
          isEmulator,
          errorName: error instanceof Error ? error.name : 'Unknown',
          errorMessage: error instanceof Error ? error.message : String(error)
        });

        // Reportar ao Sentry apenas se não for emulador
        if (!isEmulator && typeof window !== 'undefined' && (window as any).Sentry) {
          (window as any).Sentry.captureException(error, {
            tags: { service: 'tracking', context: 'background_geolocation_init' }
          });
        }

        // Toast apropriado
        if (typeof window !== 'undefined' && (window as any).toast) {
          if (isEmulator) {
            (window as any).toast.warning(
              'GPS avançado não disponível no emulador. Usando GPS básico para testes.'
            );
          } else {
            (window as any).toast.warning(
              'GPS de alta precisão indisponível. Usando GPS padrão (pode ter menor precisão).'
            );
          }
        }

        // ✅ FALLBACK IMEDIATO: GPS padrão do navegador
        logger.warn('⚠️ Falling back to standard browser geolocation');
        // Only fallback if no watcher is active yet
        if (!this.watchId) {
          await this.startStandardGeolocation();
        } else {
          logger.info('Standard geolocation already active, skipping fallback');
        }
      }
    } else {
      // Use standard geolocation for web
      await this.startStandardGeolocation();
    }
    
    // Iniciar timer de batch sync (a cada 30s)
    this.batchSyncTimer = window.setInterval(() => {
      if (this.pointsBuffer.length > 0) {
        this.batchSyncPoints();
      }
    }, 30000);
    
    this.trackEvent('run_started', { sessionId, mode: this.isNative ? 'background' : 'standard' });
    
    return { enabled: true, watchId: this.watchId || this.bgWatcherId };
  }

  private async startStandardGeolocation() {
    // Clear any existing watcher before starting new one
    if (this.watchId) {
      logger.warn('Clearing existing watchId before starting new standard geolocation', { oldWatchId: this.watchId });
      navigator.geolocation.clearWatch(Number(this.watchId));
      this.watchId = null;
    }
    
    logger.info('🚀 Starting standard browser geolocation with high accuracy');
    
    // ✅ Add safety timeout: if no GPS point after 5 seconds, alert and retry
    let firstPointReceived = false;
    const safetyTimeout = setTimeout(() => {
      if (!firstPointReceived && this.points.length === 0) {
        logger.error('❌ GPS timeout: no points received after 5 seconds');
        if (typeof window !== 'undefined' && (window as any).toast) {
          (window as any).toast.error('GPS não está respondendo. Verificando permissões...');
        }
        // Retry permission and provide guidance
        this.checkLocationPermission().then(status => {
          if (!status.granted) {
            logger.error('GPS permission was revoked during tracking');
            if (typeof window !== 'undefined' && (window as any).toast) {
              (window as any).toast.error('Permissão de GPS foi revogada. Habilite nas configurações.');
            }
          } else {
            logger.warn('Permission OK but no GPS data. Device GPS may be disabled.');
          }
        });
      }
    }, 5000);
    
    // Use browser Geolocation API
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!firstPointReceived) {
          firstPointReceived = true;
          clearTimeout(safetyTimeout);
          logger.info('✅ First GPS point received successfully', {
            lat: position.coords.latitude.toFixed(6),
            lng: position.coords.longitude.toFixed(6),
            accuracy: position.coords.accuracy.toFixed(1)
          });
        }
        
        // Convert to standard format
        const standardPosition = {
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed
          },
          timestamp: position.timestamp
        };
        
        logger.debug('GPS point captured (standard browser)', {
          lat: standardPosition.coords.latitude,
          lng: standardPosition.coords.longitude,
          accuracy: standardPosition.coords.accuracy,
          totalPoints: this.points.length + 1
        });
        
        this.handleLocation(standardPosition);
      },
      (error) => {
        clearTimeout(safetyTimeout);
        logger.error('❌ Standard geolocation error', error, {
          code: error.code,
          message: error.message,
          PERMISSION_DENIED: error.code === 1,
          POSITION_UNAVAILABLE: error.code === 2,
          TIMEOUT: error.code === 3
        });
        
        // User-friendly error messages
        if (error.code === 1) {
          if (typeof window !== 'undefined' && (window as any).toast) {
            (window as any).toast.error('Permissão de localização negada. Habilite nas configurações do dispositivo.');
          }
        } else if (error.code === 2) {
          if (typeof window !== 'undefined' && (window as any).toast) {
            (window as any).toast.error('GPS não disponível. Verifique se o GPS está ativado.');
          }
        } else if (error.code === 3) {
          logger.warn('GPS timeout, but continuing to try...');
        }
        
        this.trackEvent('gps_error', { code: error.code, message: error.message });
        
        // Report to Sentry
        if (typeof window !== 'undefined' && (window as any).Sentry) {
          (window as any).Sentry.captureException(error, {
            tags: { service: 'tracking', context: 'standard_geolocation' }
          });
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,        // ⬆️ Allow more time for first GPS fix
        maximumAge: 0         // ✅ Always use fresh location
      }
    );
    
    this.watchId = String(watchId);
    logger.info('📡 Browser geolocation watch registered', { watchId: this.watchId });
  }

  /**
   * ✅ WATCHDOG: Multi-phase fallback system for GPS initialization
   * Phase 1 (5s): Warning log - GPS still warming up
   * Phase 2 (8s): Activate Capacitor native fallback
   * Phase 3 (15s): Emergency one-shot getCurrentPosition
   */
  private startNoPointsWatchdog() {
    // Clear any existing watchdog timers
    if (this.noPointsWatchdogTimer) {
      clearTimeout(this.noPointsWatchdogTimer);
    }
    
    // ========== PHASE 1: Warning at 5 seconds ==========
    setTimeout(() => {
      if (this.points.length === 0) {
        logger.warn('⏰ WATCHDOG 5s: Still no GPS points, continuing to wait...');
        persistentLogger.addLog('GPS', 'warn', 'GPS warming up slowly (5s)', {
          timeSinceStart: 5,
          hasCapacitorFallback: !!this.capacitorFallbackWatchId
        });
      }
    }, 5000);
    
    // ========== PHASE 2: Capacitor fallback at 8 seconds ==========
    this.noPointsWatchdogTimer = setTimeout(async () => {
      if (this.points.length === 0 && !this.capacitorFallbackWatchId) {
        logger.warn('⏰ WATCHDOG 8s: No GPS points, activating Capacitor native fallback');
        persistentLogger.addLog('GPS', 'warn', 'Watchdog triggered - activating Capacitor fallback', {
          pointsCollected: 0,
          sessionStartTime: this.sessionStartTime
        });
        
        if (typeof window !== 'undefined' && (window as any).toast) {
          (window as any).toast.warning('GPS demorado. Ativando modo alternativo...');
        }
        
        await this.startCapacitorNativeFallback();
      }
    }, 8000);
    
    // ========== PHASE 3: Emergency one-shot at 15 seconds ==========
    setTimeout(async () => {
      if (this.points.length === 0) {
        logger.error('🆘 WATCHDOG 15s: EMERGENCY - Still no GPS, forcing one-shot getCurrentPosition');
        persistentLogger.addLog('GPS', 'error', 'Emergency GPS fallback activated (15s)', {
          hasCapacitorFallback: !!this.capacitorFallbackWatchId,
          hasBgWatcher: !!this.bgWatcherId
        });
        
        try {
          // Try Capacitor getCurrentPosition as last resort
          const pos = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          });
          
          if (pos) {
            logger.info('🆘 Emergency getCurrentPosition succeeded!', {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy
            });
            
            this.handleLocation({
              coords: {
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
                altitude: pos.coords.altitude,
                altitudeAccuracy: pos.coords.altitudeAccuracy,
                heading: pos.coords.heading,
                speed: pos.coords.speed
              },
              timestamp: pos.timestamp
            });
          }
        } catch (err) {
          logger.error('🆘 Emergency getCurrentPosition FAILED', err);
          persistentLogger.addLog('GPS', 'error', 'Emergency GPS fallback failed', {
            error: err instanceof Error ? err.message : String(err)
          });
          
          // Show user-facing error
          if (typeof window !== 'undefined' && (window as any).toast) {
            (window as any).toast.error('GPS não disponível. Verifique se está em local aberto e tente novamente.');
          }
        }
      }
    }, 15000);
  }

  /**
   * ✅ FALLBACK: Use @capacitor/geolocation as robust native fallback
   * This runs in parallel if TransistorSoft fails to deliver points
   */
  private async startCapacitorNativeFallback() {
    if (this.capacitorFallbackWatchId) {
      logger.info('Capacitor fallback already active, skipping');
      return;
    }
    
    logger.info('🚀 Starting Capacitor native geolocation fallback');
    
    try {
      const watchId = await Geolocation.watchPosition(
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 5000
        },
        (position, err) => {
          if (err) {
            logger.error('Capacitor fallback error', err);
            return;
          }
          
          if (position) {
            logger.debug('📍 Capacitor fallback position received', {
              lat: position.coords.latitude.toFixed(6),
              lng: position.coords.longitude.toFixed(6),
              accuracy: position.coords.accuracy
            });
            
            // Convert to standard format and handle
            this.handleLocation({
              coords: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude,
                altitudeAccuracy: position.coords.altitudeAccuracy,
                heading: position.coords.heading,
                speed: position.coords.speed
              },
              timestamp: position.timestamp
            });
          }
        }
      );
      
      this.capacitorFallbackWatchId = watchId;
      this.setPermissionStatus('granted');
      logger.info('✅ Capacitor native fallback started', { watchId });
      
      persistentLogger.addLog('GPS', 'info', 'Capacitor native fallback activated', { watchId });
      
    } catch (error) {
      logger.error('❌ Failed to start Capacitor fallback', error);
      // Last resort: try browser geolocation
      if (!this.watchId) {
        await this.startStandardGeolocation();
      }
    }
  }

  /**
   * Stop Capacitor fallback watcher
   */
  private async stopCapacitorFallback() {
    if (this.capacitorFallbackWatchId) {
      try {
        await Geolocation.clearWatch({ id: this.capacitorFallbackWatchId });
        logger.info('Capacitor fallback stopped', { watchId: this.capacitorFallbackWatchId });
      } catch (error) {
        logger.warn('Error stopping Capacitor fallback', error);
      }
      this.capacitorFallbackWatchId = null;
    }
    
    if (this.noPointsWatchdogTimer) {
      clearTimeout(this.noPointsWatchdogTimer);
      this.noPointsWatchdogTimer = null;
    }
  }

  /**
   * ⏱️ START FORCED 1Hz COLLECTION
   * Time-based GPS polling that works independently of physical movement.
   * Critical for Territory Conquest mode where GPS must start immediately.
   */
  private startForcedCollection(intervalMs: number = 1000) {
    // Clear any existing timer
    if (this.forcedCollectionTimer) {
      clearInterval(this.forcedCollectionTimer);
      this.forcedCollectionTimer = null;
    }
    
    this.forcedCollectionEnabled = true;
    logger.info('⏱️ Starting forced 1Hz GPS collection', { intervalMs });
    persistentLogger.addLog('GPS', 'info', 'Forced 1Hz collection started', { intervalMs });
    
    this.forcedCollectionTimer = setInterval(async () => {
      if (!this.forcedCollectionEnabled) return;
      
      try {
        let position: any = null;
        
        if (this.isNative) {
          // Use TransistorSoft getCurrentPosition for native
          position = await BackgroundGeolocation.getCurrentPosition({
            samples: 1,
            timeout: 2000,
            persist: false,
            maximumAge: 1000,
            desiredAccuracy: 50
          });
        } else {
          // Use Capacitor Geolocation for web fallback
          position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 2000,
            maximumAge: 1000
          });
        }
        
        if (position) {
          // Convert and handle (deduplication happens in handleLocation)
          this.handleLocation({
            coords: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              altitude: position.coords.altitude,
              altitudeAccuracy: position.coords.altitude_accuracy ?? position.coords.altitudeAccuracy,
              heading: position.coords.heading,
              speed: position.coords.speed
            },
            timestamp: position.timestamp || Date.now()
          });
        }
      } catch (err) {
        // Silent - the watcher is still active as primary source
        // This is just a supplementary collection mechanism
        logger.debug('Forced collection poll failed (expected if watcher is working)', err);
      }
    }, intervalMs);
    
    logger.info('⏱️ Forced 1Hz collection timer started');
  }

  /**
   * ⏱️ STOP FORCED COLLECTION
   */
  private stopForcedCollection() {
    if (this.forcedCollectionTimer) {
      clearInterval(this.forcedCollectionTimer);
      this.forcedCollectionTimer = null;
    }
    this.forcedCollectionEnabled = false;
    logger.info('⏱️ Stopped forced GPS collection');
    persistentLogger.addLog('GPS', 'info', 'Forced 1Hz collection stopped', {});
  }

  async pauseTracking() {
    logger.info('⏸️ Pausing tracking (watchId will be preserved)', {
      hasWatchId: !!this.watchId,
      hasBgWatcherId: !!this.bgWatcherId,
      pointsCollected: this.points.length
    });
    
    // ✅ FIX: Stop the GPS watcher but DON'T clear the IDs yet
    // This allows us to resume properly without losing session state
    if (this.bgWatcherId && this.isNative) {
      await BackgroundGeolocation.stop();
      // Keep bgWatcherId for resume
      logger.info('TransistorSoft background geolocation paused (bgWatcherId preserved)');
    }
    
    if (this.watchId) {
      if (typeof navigator !== 'undefined' && typeof navigator.geolocation?.clearWatch === 'function') {
        navigator.geolocation.clearWatch(Number(this.watchId));
      }
      // Keep watchId for resume
      logger.info('Browser geolocation paused (watchId preserved)');
    }
    
    // ⏱️ Pause forced 1Hz collection
    this.stopForcedCollection();
    
    this.trackEvent('run_paused');
  }

  async resumeTracking() {
    logger.info('▶️ Resuming tracking', {
      hasSessionId: !!this.sessionId,
      hasWatchId: !!this.watchId,
      hasBgWatcherId: !!this.bgWatcherId,
      pointsCollected: this.points.length
    });
    
    // ✅ FIX: Restart GPS watchers using existing session
    if (this.isNative && this.bgWatcherId) {
      await BackgroundGeolocation.start();
      logger.info('TransistorSoft background geolocation resumed');
    } else if (this.isNative && !this.bgWatcherId) {
      // If bgWatcherId was lost, reinitialize properly
      logger.warn('bgWatcherId was lost, reinitializing...');
      await BackgroundGeolocation.start();
      this.bgWatcherId = 'transistorsoft-active';
    } else if (this.watchId) {
      // Restart standard geolocation with same session
      await this.startStandardGeolocation();
      logger.info('Standard geolocation resumed');
    } else {
      // Last resort: restart completely (should not happen if pause worked correctly)
      logger.warn('No active watchers found, restarting tracking...');
      if (this.sessionId) {
        await this.startTracking(this.sessionId);
      }
    }
    
    // ⏱️ Resume forced 1Hz collection
    this.startForcedCollection(1000);
    
    this.trackEvent('run_resumed');
  }

  /**
   * Restart tracking without clearing existing points
   * Used when resuming from background/visibility change
   */
  async restartTracking() {
    if (!this.sessionId) {
      logger.error('Cannot restart tracking without active session');
      throw new Error('Cannot restart tracking without active session');
    }
    
    // ✅ CRITICAL: Attempt to hydrate points from backup BEFORE restarting
    if (this.points.length === 0) {
      await this.hydratePointsFromBackup();
    }
    
    logger.info('Restarting tracking (preserving points)', {
      sessionId: this.sessionId,
      existingPoints: this.points.length,
      isNative: this.isNative
    });
    
    // Reiniciar watchers SEM limpar pontos
    if (this.isNative) {
      try {
        await BackgroundGeolocation.start();
        this.bgWatcherId = 'transistorsoft-active';
        logger.info('TransistorSoft background geolocation restarted');
      } catch (error) {
        logger.error('Failed to restart TransistorSoft, falling back to standard geolocation', error);
        await this.startStandardGeolocation();
      }
    } else {
      await this.startStandardGeolocation();
    }
    
    // Restart batch sync timer
    if (!this.batchSyncTimer) {
      this.batchSyncTimer = window.setInterval(() => {
        if (this.pointsBuffer.length > 0) {
          this.batchSyncPoints();
        }
      }, 30000);
    }
    
    logger.info('Tracking restarted successfully', { 
      sessionId: this.sessionId,
      preservedPoints: this.points.length 
    });
  }

  /**
   * Hydrate points from backup sources (sessionStorage, IndexedDB)
   * Used when resuming from background and in-memory points are lost
   */
  async hydratePointsFromBackup(): Promise<boolean> {
    logger.info('🔄 Attempting to hydrate points from backup...');
    
    // 1. Try sessionStorage first (fastest)
    try {
      const backup = sessionStorage.getItem('gps_points_backup');
      if (backup) {
        const backedUpPoints: TrackingPoint[] = JSON.parse(backup);
        if (backedUpPoints.length > 0) {
          // Validate timestamps are proper epoch ms
          const validatedPoints = backedUpPoints.map(p => ({
            ...p,
            timestamp: typeof p.timestamp === 'string' 
              ? new Date(p.timestamp).getTime() 
              : p.timestamp
          }));
          
          this.points = validatedPoints;
          logger.info('✅ Hydrated points from sessionStorage backup', { 
            count: validatedPoints.length 
          });
          persistentLogger.addLog('GPS', 'info', 'Points hydrated from sessionStorage', {
            count: validatedPoints.length
          });
          return true;
        }
      }
    } catch (error) {
      logger.warn('Failed to hydrate from sessionStorage', error);
    }
    
    // 2. Try IndexedDB (more persistent)
    if (this.sessionId) {
      try {
        const dbPoints = await this.recoverSession(this.sessionId);
        if (dbPoints.length > 0) {
          this.points = dbPoints;
          logger.info('✅ Hydrated points from IndexedDB', { count: dbPoints.length });
          persistentLogger.addLog('GPS', 'info', 'Points hydrated from IndexedDB', {
            count: dbPoints.length
          });
          return true;
        }
      } catch (error) {
        logger.warn('Failed to hydrate from IndexedDB', error);
      }
    }
    
    logger.warn('⚠️ No backup points found to hydrate');
    return false;
  }

  /**
   * Manually set points (used for session recovery)
   * Only works if there's an active session
   */
  hydratePoints(points: TrackingPoint[]): boolean {
    if (!this.sessionId) {
      logger.warn('Cannot hydrate points without active session');
      return false;
    }
    
    if (points.length === 0) {
      logger.warn('No points provided for hydration');
      return false;
    }
    
    // Validate and sanitize timestamps
    const validatedPoints = points.map(p => ({
      ...p,
      timestamp: typeof p.timestamp === 'string' 
        ? new Date(p.timestamp).getTime() 
        : p.timestamp
    }));
    
    this.points = validatedPoints;
    
    // Notify listeners about the last point for immediate UI update
    if (validatedPoints.length > 0) {
      this.notifyLocationListeners(validatedPoints[validatedPoints.length - 1]);
    }
    
    logger.info('✅ Points hydrated manually', { count: validatedPoints.length });
    return true;
  }

  /**
   * Get the last known position (for map centering fallback)
   */
  getLastKnownPosition(): { lat: number; lng: number } | null {
    // Priority 1: Current session points
    if (this.points.length > 0) {
      const lastPoint = this.points[this.points.length - 1];
      return { lat: lastPoint.lat, lng: lastPoint.lng };
    }
    
    // Priority 2: Session backup
    try {
      const backup = sessionStorage.getItem('gps_points_backup');
      if (backup) {
        const points = JSON.parse(backup);
        if (points.length > 0) {
          const lastPoint = points[points.length - 1];
          return { lat: lastPoint.lat, lng: lastPoint.lng };
        }
      }
    } catch (e) {
      // Ignore
    }
    
    // ⚡ Priority 3: localStorage persistent position (for instant map on next run)
    try {
      const lastKnown = localStorage.getItem('lastKnownGPSPosition');
      if (lastKnown) {
        const parsed = JSON.parse(lastKnown);
        // Only use if less than 1 hour old
        if (parsed.lat && parsed.lng && (Date.now() - parsed.timestamp) < 3600000) {
          return { lat: parsed.lat, lng: parsed.lng };
        }
      }
    } catch (e) {
      // Ignore
    }
    
    return null;
  }

  async stopTracking() {
    // Try to recover from backup if points are empty
    if (this.points.length === 0) {
      logger.warn('⚠️ No points in memory, attempting recovery from backup');
      persistentLogger.addLog('GPS', 'warn', 'No points, attempting recovery', {});
      
      try {
        const backup = sessionStorage.getItem('gps_points_backup');
        if (backup) {
          const backedUpPoints: TrackingPoint[] = JSON.parse(backup);
          if (backedUpPoints.length > 0) {
            this.points = backedUpPoints;
            logger.info('✅ Recovered points from backup', { count: backedUpPoints.length });
            persistentLogger.addLog('GPS', 'info', 'Points recovered from backup', { 
              count: backedUpPoints.length 
            });
          }
        }
      } catch (error) {
        logger.error('Failed to recover points from backup', error);
        persistentLogger.addLog('GPS', 'error', 'Backup recovery failed', { error });
      }
    }
    
    // ✅ CRITICAL: Capture points BEFORE any clearing operations
    const finalPoints = [...this.points];
    const finalMetrics = { ...this.metrics };
    const capturedSessionId = this.sessionId;
    
    logger.info('stopTracking called', {
      pointsToReturn: finalPoints.length,
      sessionId: capturedSessionId,
      currentPoints: this.points.length
    });
    
    persistentLogger.addLog('GPS', 'info', 'GPS tracking stopped', {
      sessionId: capturedSessionId,
      totalPoints: finalPoints.length,
      rejectedByAccuracy: this.rejectedPointsLog.accuracy,
      rejectedBySpeed: this.rejectedPointsLog.speed,
      rejectedByDistance: this.rejectedPointsLog.distance
    });

    if (this.bgWatcherId && this.isNative) {
      await BackgroundGeolocation.stop();
      this.bgWatcherId = null;
      logger.info('TransistorSoft background geolocation stopped');
    }

    if (this.watchId) {
      navigator.geolocation.clearWatch(Number(this.watchId));
      this.watchId = null;
      logger.info('Browser geolocation stopped');
    }
    
    // ✅ Stop Capacitor fallback if active
    await this.stopCapacitorFallback();
    
    // ⏱️ Stop forced 1Hz collection
    this.stopForcedCollection();
    
    // Parar timer de batch sync
    if (this.batchSyncTimer) {
      clearInterval(this.batchSyncTimer);
      this.batchSyncTimer = null;
    }
    
    // Enviar pontos restantes no buffer
    if (this.pointsBuffer.length > 0) {
      await this.batchSyncPoints();
    }
    
    // ✅ DEFENSIVE VALIDATION: Check if we're losing points
    if (finalPoints.length === 0 && capturedSessionId) {
      logger.error('CRITICAL: stopTracking returning 0 points!', {
        sessionId: capturedSessionId,
        currentPointsBeforeClear: this.points.length,
        checkSessionStorage: sessionStorage.getItem('gps_points_backup')
      });
      
      // Try to recover from backup
      try {
        const backup = sessionStorage.getItem('gps_points_backup');
        if (backup) {
          const backupPoints = JSON.parse(backup);
          logger.warn('Recovering points from sessionStorage backup', {
            backupPointsCount: backupPoints.length
          });
          // Return backup points instead
          return { points: backupPoints, metrics: finalMetrics };
        }
      } catch (backupError) {
        logger.error('Failed to recover from backup', backupError);
      }
    }
    
    logger.info('Tracking stopped', {
      totalPoints: finalPoints.length,
      p95Error: finalMetrics.p95Error,
      gapPercentage: finalMetrics.gapPercentage
    });
    
    // Clear backup
    sessionStorage.removeItem('gps_points_backup');
    
    this.sessionId = null;
    this.points = [];
    this.pointsBuffer = [];
    this.locationListeners = []; // Clear location listeners
    
    this.trackEvent('run_ended', {
      totalPoints: finalPoints.length,
      metrics: finalMetrics
    });
    
    return { points: finalPoints, metrics: finalMetrics };
  }
  
  /**
   * Subscribe to location updates for instant UI updates (Strava-class performance)
   * Returns unsubscribe function
   */
  onLocationUpdate(callback: (point: TrackingPoint) => void): () => void {
    this.locationListeners.push(callback);
    return () => {
      this.locationListeners = this.locationListeners.filter(cb => cb !== callback);
    };
  }
  
  private notifyLocationListeners(point: TrackingPoint): void {
    this.locationListeners.forEach(cb => {
      try { 
        cb(point); 
      } catch (e) { 
        console.error('[TrackingService] Location listener error', e); 
      }
    });
  }

  async exportGPX(): Promise<string> {
    // Generate GPX from points
    const gpx = this.generateGPX(this.points);
    this.trackEvent('gpx_exported', { pointCount: this.points.length });
    return gpx;
  }

  async exportFIT(): Promise<Blob> {
    // Generate FIT file from points
    const fit = this.generateFIT(this.points);
    this.trackEvent('fit_exported', { pointCount: this.points.length });
    return fit;
  }

  private async batchSyncPoints() {
    if (this.pointsBuffer.length === 0) return;
    
    const pointsToSync = [...this.pointsBuffer];
    this.pointsBuffer = []; // Limpar buffer imediatamente
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const records = pointsToSync.map(point => ({
        session_id: this.sessionId,
        user_id: user.id,
        lat: point.lat,
        lng: point.lng,
        timestamp: point.timestamp,
        speed: point.speed,
        accuracy: point.accuracy,
        altitude: point.altitude
      }));

      await supabase.from('tracking_points_buffer').insert(records);
      
      this.trackEvent('gps_points_synced', { count: pointsToSync.length });
    } catch (error) {
      logger.error('Failed to batch sync points', error, {
        component: 'TrackingService',
        action: 'batchSyncPoints',
        pointsCount: pointsToSync.length
      });
      this.trackEvent('gps_sync_error', { error: String(error) });
      
      // Reportar ao Sentry se for erro crítico
      if (typeof window !== 'undefined' && (window as any).Sentry) {
        (window as any).Sentry.captureException(error, {
          tags: { service: 'tracking', action: 'batch_sync' },
          extra: { pointsCount: pointsToSync.length }
        });
      }
    }
  }

  private updateMetrics(position: any) {
    const accuracy = position.coords.accuracy;
    
    // Coletar histórico de accuracies
    this.accuracyHistory.push(accuracy);
    
    // Calcular P95 (percentil 95)
    if (this.accuracyHistory.length >= 20) {
      const sorted = [...this.accuracyHistory].sort((a, b) => a - b);
      const p95Index = Math.floor(sorted.length * 0.95);
      this.metrics.p95Error = sorted[p95Index];
    } else {
      this.metrics.p95Error = accuracy;
    }
    
    // Limitar histórico a últimos 100 pontos
    if (this.accuracyHistory.length > 100) {
      this.accuracyHistory.shift();
    }

    // Calculate gap percentage
    if (this.points.length > 1) {
      let totalGaps = 0;
      for (let i = 1; i < this.points.length; i++) {
        const timeDiff = this.points[i].timestamp - this.points[i - 1].timestamp;
        if (timeDiff > 10000) { // Gap > 10s
          totalGaps++;
        }
      }
      this.metrics.gapPercentage = (totalGaps / this.points.length) * 100;
    }

    this.trackEvent('gps_quality', this.metrics);
  }

  private generateGPX(points: TrackingPoint[]): string {
    // GPX XML generation
    let gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="YouBetterRun">
  <trk>
    <name>Run ${new Date().toISOString()}</name>
    <trkseg>`;

    points.forEach(p => {
      gpx += `
      <trkpt lat="${p.lat}" lon="${p.lng}">
        <ele>${p.altitude || 0}</ele>
        <time>${new Date(p.timestamp).toISOString()}</time>
        <extensions>
          <speed>${p.speed}</speed>
          <accuracy>${p.accuracy}</accuracy>
        </extensions>
      </trkpt>`;
    });

    gpx += `
    </trkseg>
  </trk>
</gpx>`;

    return gpx;
  }

  private generateFIT(points: TrackingPoint[]): Blob {
    try {
      // TODO: Implementar FIT encoder real usando fit-file-writer
      // Por enquanto, gerar um arquivo JSON como placeholder
      const fitData = {
        fileType: 'activity',
        sport: 'running',
        timeCreated: new Date().toISOString(),
        records: points.map(p => ({
          timestamp: new Date(p.timestamp).toISOString(),
          positionLat: p.lat,
          positionLong: p.lng,
          altitude: p.altitude || 0,
          speed: p.speed,
          distance: 0 // Será calculado
        }))
      };
      
      const json = JSON.stringify(fitData, null, 2);
      return new Blob([json], { type: 'application/json' });
    } catch (error) {
      logger.error('FIT generation failed', error as Error);
      throw error;
    }
  }

  private trackEvent(event: string, data?: any) {
    logger.info(`TrackingService Event: ${event}`, data);
    
    // Send to analytics
    if (typeof window !== 'undefined' && (window as any).analytics) {
      (window as any).analytics.track(event, data);
    }
  }

  getMetrics(): TrackingMetrics {
    return { ...this.metrics };
  }

  getCurrentPoints(): TrackingPoint[] {
    return [...this.points];
  }

  getRejectedPointsStats() {
    return {
      ...this.rejectedPointsLog,
      total: this.rejectedPointsLog.accuracy + this.rejectedPointsLog.speed + this.rejectedPointsLog.distance
    };
  }

  private async persistPoint(point: TrackingPoint) {
    if (!this.storage || !this.sessionId) return;
    
    try {
      await this.storage.add('points', { ...point, sessionId: this.sessionId });
    } catch (error) {
      logger.error('Failed to persist tracking point', error as Error, {
        sessionId: this.sessionId
      });
    }
  }

  /**
   * 🧪 TEST ONLY: Inject GPS position manually (for testing)
   */
  public emitPosition(point: MockGPSPoint): void {
    if (!this.points || this.points.length === 0 || (typeof import.meta !== 'undefined' && import.meta.env.PROD)) {
      logger.warn('emitPosition called but tracking inactive or in production', {
        hasPoints: this.points.length > 0,
        isProd: typeof import.meta !== 'undefined' ? import.meta.env.PROD : false
      });
      return;
    }

    const trackingPoint: TrackingPoint = {
      lat: point.lat,
      lng: point.lng,
      timestamp: point.timestamp || Date.now(),
      speed: point.speed ?? 0,
      accuracy: point.accuracy ?? 10,
      altitude: point.altitude
    };

    // Process point through same pipeline as real GPS
    this.handleLocation({
      coords: {
        latitude: trackingPoint.lat,
        longitude: trackingPoint.lng,
        accuracy: trackingPoint.accuracy,
        speed: trackingPoint.speed,
        altitude: trackingPoint.altitude ?? null,
        altitudeAccuracy: null,
        heading: null
      },
      timestamp: trackingPoint.timestamp
    } as any);

    logger.debug('Test position injected', { point: trackingPoint });
  }

  async recoverSession(sessionId: string): Promise<TrackingPoint[]> {
    if (!this.storage) {
      await this.initDB();
    }
    
    if (!this.storage) return [];

    try {
      // Use storage adapter if possible
      const points = await this.storage.getAll('points');
      // Filter by sessionId and strip sessionId field
      return points
        .filter((p: any) => p.sessionId === sessionId)
        .map(({ sessionId: _, ...point }: any) => point);
    } catch (error) {
      logger.error('Failed to recover session', error as Error, { sessionId });
      return [];
    }
  }

  /**
   * Check location permission status
   * ✅ Added for explicit permission validation before tracking
   */
  private async checkLocationPermission(): Promise<{ granted: boolean; status?: string }> {
    try {
      if (this.isNative) {
        // For native iOS, check with BackgroundGeolocation
        const status = await BackgroundGeolocation.getProviderState();
        logger.info('Native GPS permission check', { 
          enabled: status.enabled,
          gps: status.gps,
          status: status.status 
        });
        
        return {
          granted: status.enabled && status.gps,
          status: JSON.stringify(status)
        };
      } else {
        // For web, check with Permissions API if available
        if ('permissions' in navigator) {
          const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
          logger.info('Web GPS permission check', { state: result.state });
          return {
            granted: result.state === 'granted',
            status: result.state
          };
        } else {
          // Fallback: assume granted if Permissions API not available
          logger.warn('Permissions API not available, assuming granted');
          return { granted: true, status: 'assumed' };
        }
      }
    } catch (error) {
      logger.error('Permission check failed', error);
      // If permission check fails, assume denied to be safe
      return { granted: false, status: 'error' };
    }
  }

  async clearPersistedSession(sessionId: string) {
    if (!this.db) return;

    try {
      const tx = this.db.transaction('points', 'readwrite');
      const index = tx.store.index('by-session');
      const keys = await index.getAllKeys(sessionId);
      
      for (const key of keys) {
        await tx.store.delete(key);
      }
      
      await tx.done;
    } catch (error) {
      logger.error('Failed to clear persisted session', error as Error, { sessionId });
    }
  }

  /**
   * Get TransistorSoft plugin health status
   * Returns null if not running on native platform
   */
  async getTransistorSoftHealth(): Promise<{
    isEnabled: boolean;
    isMoving: boolean;
    providerState: any;
    odometer: number;
  } | null> {
    if (!this.isNative) {
      return null;
    }
    
    try {
      const state = await BackgroundGeolocation.getState();
      const providerState = await BackgroundGeolocation.getProviderState();
      const odometer = await BackgroundGeolocation.getOdometer();
      
      return {
        isEnabled: state.enabled,
        isMoving: state.isMoving,
        providerState,
        odometer
      };
    } catch (error) {
      logger.error('Failed to get TransistorSoft health', error as Error);
      return null;
    }
  }
}

export const trackingService = new TrackingService();

// Export nomeado para testes (undefined fora do ambiente de teste)
export let __testOnly__: { injectPosition: (point: MockGPSPoint) => void } | undefined;

// Test-only utilities for injecting GPS positions without native APIs
if (typeof import.meta.env !== 'undefined' && 
    (import.meta.env.MODE === 'test' || 
     import.meta.env.VITEST === true || 
     import.meta.env.DEV === true)) {
  
  __testOnly__ = {
    injectPosition: (point: MockGPSPoint) => {
      const location: Location = {
        coords: {
          latitude: point.lat,
          longitude: point.lng,
          accuracy: point.accuracy ?? 10,
          speed: point.speed ?? 0,
          altitude: point.altitude ?? 0,
          heading: 0,
          altitude_accuracy: 0
        },
        timestamp: `${point.timestamp ?? Date.now()}`,
        activity: { type: 'running', confidence: 100 },
        battery: { level: 0.8, is_charging: false },
        is_moving: true,
        uuid: `test-${Date.now()}`,
        odometer: 0,
        event: 'location',
        age: 0
      };
      
      // Use private method to process location
      (trackingService as any).handleLocation(location);
    }
  };

  // Mantém compatibilidade com acessos via instância
  (trackingService as any).__testOnly__ = __testOnly__;
}
export type { TrackingPoint, TrackingMetrics, MockGPSPoint };
